import React, { useState } from 'react';
import { Button, Form, Input, Select, Typography } from 'antd';
import { BankOutlined, GlobalOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { IQualificationConfig, IQualificationOption } from '../../services/nestjs-base-api/types';
import {
  resolveByCategory,
  resolveByModelAndCategory,
} from '../../utils/qualificationResolver';
import { normalizeWebsiteUrl } from '../../utils/websiteUrl';

const { Text, Title } = Typography;

export interface QualificationAnswers {
  brandName: string;
  websiteUrl?: string;
  productCategory?: string;
  businessModel?: string;
}

export interface ResolvedQualification {
  productCategory: string;
  businessModel?: string;
  templateType: string;
}

interface QualificationStepProps {
  isMobile: boolean;
  config?: IQualificationConfig | null;
  initialBrandName?: string;
  initialWebsiteUrl?: string;
  onQualified: (answers: QualificationAnswers, resolved: ResolvedQualification) => void;
  onNotQualified: (
    answers: QualificationAnswers,
    productCategory?: string,
    businessModel?: string,
  ) => void;
}

type ScreenIndex = 0 | 1 | 2;

const PRIMARY_GRADIENT = 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)';
const PRIMARY_SHADOW = '0 4px 14px 0 rgba(245, 158, 11, 0.4)';

export const QualificationStep: React.FC<QualificationStepProps> = ({
  isMobile,
  config,
  initialBrandName,
  initialWebsiteUrl,
  onQualified,
  onNotQualified,
}) => {
  const intl = useIntl();
  const [screen, setScreen] = useState<ScreenIndex>(0);
  const [brandName, setBrandName] = useState<string>(initialBrandName || '');
  const [websiteUrl, setWebsiteUrl] = useState<string>(initialWebsiteUrl || '');
  const [productCategory, setProductCategory] = useState<string | undefined>(undefined);
  const [businessModel, setBusinessModel] = useState<string | undefined>(undefined);
  const [eligibleModels, setEligibleModels] = useState<IQualificationOption[]>([]);
  const [form] = Form.useForm();

  const categories = config?.productCategories || [];

  const goBack = () => {
    if (screen === 0) return;
    setScreen((screen - 1) as ScreenIndex);
  };

  // "mybrand.com" is a perfectly good answer — add the scheme for the user
  // rather than rejecting them at the first field of the funnel.
  const handleDetailsSubmit = (values: { brandName: string; websiteUrl?: string }) => {
    setBrandName(values.brandName.trim());
    setWebsiteUrl(normalizeWebsiteUrl(values.websiteUrl || ''));
    setScreen(1);
  };

  const currentAnswers = (): QualificationAnswers => ({
    brandName,
    websiteUrl: websiteUrl || undefined,
    productCategory,
    businessModel,
  });

  const handleCategoryContinue = () => {
    if (!config || !productCategory) return;
    const result = resolveByCategory(config, productCategory);
    if (!result.supported) {
      onNotQualified(currentAnswers(), productCategory);
      return;
    }
    if (result.needsModel) {
      setEligibleModels(result.eligibleModels || []);
      setBusinessModel(undefined);
      setScreen(2);
      return;
    }
    onQualified(currentAnswers(), {
      productCategory,
      templateType: result.templateType as string,
    });
  };

  const handleModelContinue = () => {
    if (!config || !productCategory || !businessModel) return;
    const result = resolveByModelAndCategory(config, businessModel, productCategory);
    const answers: QualificationAnswers = {
      brandName,
      websiteUrl: websiteUrl || undefined,
      productCategory,
      businessModel,
    };
    if (!result.supported) {
      onNotQualified(answers, productCategory, businessModel);
      return;
    }
    onQualified(answers, {
      productCategory,
      businessModel,
      templateType: result.templateType as string,
    });
  };

  const renderProgressHeader = (showBack: boolean) => (
    <div style={{ marginBottom: isMobile ? '1.5rem' : '2rem', textAlign: 'center' }}>
      {showBack && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '0.5rem' }}>
          <Button
            type="text"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={goBack}
            style={{ color: '#6b7280' }}
          >
            {intl.formatMessage({ id: 'auth.qualification.backButton' })}
          </Button>
        </div>
      )}
    </div>
  );

  const renderQuestionTitle = (title: string, description: string) => (
    <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
      <Title
        level={2}
        style={{
          color: '#1f2937',
          fontWeight: 700,
          marginBottom: '0.75rem',
          fontSize: isMobile ? '24px' : '28px',
        }}
      >
        {title}
      </Title>
      <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '15px', lineHeight: 1.6, display: 'block' }}>
        {description}
      </Text>
    </div>
  );

  const primaryButtonStyle: React.CSSProperties = {
    height: isMobile ? '52px' : '48px',
    borderRadius: '12px',
    background: PRIMARY_GRADIENT,
    border: 'none',
    fontSize: '16px',
    fontWeight: 600,
    boxShadow: PRIMARY_SHADOW,
  };

  const signinFooter = (
    <div style={{ textAlign: 'center', marginTop: isMobile ? '1.5rem' : '2rem' }}>
      <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
        {intl.formatMessage({ id: 'auth.signup.hasAccount' })}
      </Text>{' '}
      <Link
        to="/sign-in"
        style={{ color: '#F59E0B', fontWeight: 600, fontSize: isMobile ? '14px' : '16px', textDecoration: 'none' }}
      >
        {intl.formatMessage({ id: 'auth.signup.signinLink' })}
      </Link>
    </div>
  );

  // Screen 0 — brand details
  if (screen === 0) {
    return (
      <>
        {renderProgressHeader(false)}
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <Title
            level={2}
            style={{
              color: '#1f2937',
              fontWeight: 700,
              marginBottom: '0.5rem',
              fontSize: isMobile ? '24px' : '30px',
            }}
          >
            {intl.formatMessage({ id: 'auth.qualification.title' })}
          </Title>
          <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
            {intl.formatMessage({ id: 'auth.qualification.description' })}
          </Text>
        </div>

        <Form
          form={form}
          name="qualification_details"
          layout="vertical"
          requiredMark={false}
          size="large"
          initialValues={{ brandName, websiteUrl }}
          onFinish={handleDetailsSubmit}
        >
          <Form.Item
            name="brandName"
            label={
              <span style={{ fontWeight: 600, color: '#374151' }}>
                {intl.formatMessage({ id: 'auth.qualification.brandNameLabel' })}
              </span>
            }
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'auth.qualification.brandNameRequired' }),
              },
            ]}
          >
            <Input
              prefix={<BankOutlined style={{ color: '#9ca3af' }} />}
              placeholder={intl.formatMessage({ id: 'auth.qualification.brandNamePlaceholder' })}
              style={{
                height: isMobile ? '52px' : '48px',
                borderRadius: '12px',
                border: '2px solid #e5e7eb',
                fontSize: '16px',
              }}
            />
          </Form.Item>

          <Form.Item
            name="websiteUrl"
            label={
              <span style={{ fontWeight: 600, color: '#374151' }}>
                {intl.formatMessage({ id: 'auth.qualification.websiteUrlLabel' })}
              </span>
            }
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'auth.qualification.websiteUrlRequired' }),
              },
              {
                validator: (_rule, value: string) => {
                  const trimmed = (value || '').trim();
                  if (!trimmed) return Promise.resolve(); // required rule covers empty
                  try {
                    const url = new URL(normalizeWebsiteUrl(trimmed));
                    return url.hostname.includes('.')
                      ? Promise.resolve()
                      : Promise.reject(new Error());
                  } catch {
                    return Promise.reject(new Error());
                  }
                },
                message: intl.formatMessage({ id: 'auth.qualification.websiteUrlInvalid' }),
              },
            ]}
          >
            <Input
              prefix={<GlobalOutlined style={{ color: '#9ca3af' }} />}
              placeholder={intl.formatMessage({ id: 'auth.qualification.websiteUrlPlaceholder' })}
              style={{
                height: isMobile ? '52px' : '48px',
                borderRadius: '12px',
                border: '2px solid #e5e7eb',
                fontSize: '16px',
              }}
            />
          </Form.Item>

          <Form.Item>
            <Button block type="primary" htmlType="submit" style={primaryButtonStyle}>
              {intl.formatMessage({ id: 'auth.qualification.continueButton' })}
            </Button>
          </Form.Item>
        </Form>

        {signinFooter}
      </>
    );
  }

  // Screen 1 — product category
  if (screen === 1) {
    return (
      <>
        {renderProgressHeader(true)}
        {renderQuestionTitle(
          intl.formatMessage({
            id: 'auth.qualification.category.title',
            defaultMessage: 'What does your business offer?',
          }),
          intl.formatMessage({
            id: 'auth.qualification.category.description',
            defaultMessage: 'Choose the category that best describes what you sell.',
          }),
        )}
        <Select
          size="large"
          showSearch
          optionFilterProp="label"
          value={productCategory}
          onChange={(v) => setProductCategory(v)}
          placeholder={intl.formatMessage({
            id: 'auth.qualification.category.placeholder',
            defaultMessage: 'Select a category',
          })}
          style={{ width: '100%', marginBottom: '1.25rem' }}
          options={categories.map((c) => ({ value: c.value, label: c.label }))}
        />
        <Button
          block
          type="primary"
          size="large"
          style={primaryButtonStyle}
          disabled={!productCategory}
          onClick={handleCategoryContinue}
        >
          {intl.formatMessage({ id: 'auth.qualification.continueButton' })}
        </Button>
        {signinFooter}
      </>
    );
  }

  // Screen 2 — business model (only when the category needs disambiguation)
  return (
    <>
      {renderProgressHeader(true)}
      {renderQuestionTitle(
        intl.formatMessage({
          id: 'auth.qualification.model.title',
          defaultMessage: 'How do you reach your customers?',
        }),
        intl.formatMessage({
          id: 'auth.qualification.model.description',
          defaultMessage: 'This helps us tailor the right question set for you.',
        }),
      )}
      <Select
        size="large"
        value={businessModel}
        onChange={(v) => setBusinessModel(v)}
        placeholder={intl.formatMessage({
          id: 'auth.qualification.model.placeholder',
          defaultMessage: 'Select a business model',
        })}
        style={{ width: '100%', marginBottom: '1.25rem' }}
        options={eligibleModels.map((m) => ({ value: m.value, label: m.label }))}
      />
      <Button
        block
        type="primary"
        size="large"
        style={primaryButtonStyle}
        disabled={!businessModel}
        onClick={handleModelContinue}
      >
        {intl.formatMessage({ id: 'auth.qualification.continueButton' })}
      </Button>
      {signinFooter}
    </>
  );
};
